import React, { forwardRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Calendar, Share2, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/useLanguage';
import { NewsItem } from '@/hooks/useNewsFeed';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';

interface NewsCardProps {
  item: NewsItem;
  index: number;
  formatDate: (dateString: string) => string;
}

export const NewsCard = forwardRef<HTMLDivElement, NewsCardProps>(({ item, index, formatDate }, ref) => {
  const { t } = useLanguage();

  // Determine accent color based on source hash (simple deterministic color)
  const getAccentColor = (source: string) => {
    if (!source) return 'border-l-gray-500';
    const colors = [
      'border-l-red-500',
      'border-l-blue-500',
      'border-l-green-500',
      'border-l-purple-500',
      'border-l-orange-500',
      'border-l-cyan-500'
    ];
    const hash = source.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const source = item.source || 'Unknown';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="h-full"
    >
      <Card
        className={cn(
          "group relative h-full flex flex-col overflow-hidden border-border/50 bg-card/30 backdrop-blur-sm transition-all duration-300",
          "hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-1 hover:bg-card/50",
          getAccentColor(source),
          "border-l-4"
        )}
      >
        <div className="p-6 flex flex-col h-full">
          <div className="flex justify-between items-center mb-4">
            <Badge
              variant="outline"
              className={cn(
                "transition-colors font-medium border-l-2",
                // Map the border color class to text/bg/border colors for the badge
                getAccentColor(source).replace('border-l-', 'text-').replace('500', '600 dark:text-').replace('border-l-', 'border-').replace('500', '200 dark:border-').replace('border-l-', 'bg-').replace('500', '50 dark:bg-') + '900/10'
              )}
            >
              {source}
            </Badge>
            <div className="flex items-center text-xs text-muted-foreground">
              <Calendar className="h-3 w-3 mr-1" />
              {formatDate(item.pubDate)}
            </div>
          </div>

          <h3 className="text-xl font-bold mb-3 line-clamp-3 group-hover:text-primary transition-colors duration-300 leading-tight">
            {item.title}
          </h3>

          {item.description && (
            <p className="text-sm text-muted-foreground mb-6 line-clamp-3 leading-relaxed">
              {item.description}
            </p>
          )}

          <div className="mt-auto pt-4 border-t border-border/30 flex items-center justify-between">
            <Button
              variant="link"
              className="p-0 h-auto font-semibold text-foreground group-hover:text-primary transition-colors"
              asChild
            >
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                {t('news.read_more')}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Button>

            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="h-8 w-8 rounded-full bg-secondary/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-50 group-hover:scale-100 cursor-pointer hover:bg-primary hover:text-primary-foreground"
              aria-label={t('news.read_more')}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Decorative gradient glow on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      </Card>
    </motion.div>
  );
});

NewsCard.displayName = "NewsCard";
